import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useFeatureFlagEnforced } from "@/hooks/useFeatureFlag";

const checkExistingSurvey = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from("feedback_submissions")
    .select("id")
    .eq("user_id", userId)
    .eq("source", "generation_gate")
    .limit(1);

  if (error) {
    console.warn("Survey completion check failed", error);
    // Fail safe: never re-gate a user because of a transient failure.
    return true;
  }
  return (data?.length ?? 0) > 0;
};

export interface UseGenerationSurveyGateResult {
  loading: boolean;
  needsSurvey: boolean;
  markCompleted: () => void;
}

export const useGenerationSurveyGate = (
  userId: string | null | undefined,
  totalGenerations: number | null | undefined
): UseGenerationSurveyGateResult => {
  const { loading: flagLoading, enforced } = useFeatureFlagEnforced(
    "mandatory_generation_survey"
  );
  const [loading, setLoading] = useState(true);
  const [hasSurvey, setHasSurvey] = useState(false);
  const [localCompleted, setLocalCompleted] = useState(false);
  const checkedForRef = useRef<string | null>(null);

  const markCompleted = useCallback(() => {
    setLocalCompleted(true);
    setHasSurvey(true);
  }, []);

  useEffect(() => {
    if (flagLoading || !userId) {
      setLoading(true);
      return;
    }
    // Run the server check once per user.
    if (checkedForRef.current === userId) return;
    checkedForRef.current = userId;

    setLoading(true);
    checkExistingSurvey(userId)
      .then((completed) => {
        setHasSurvey(completed);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [flagLoading, userId]);

  // If the user has already submitted in this session, bypass the server check.
  const isCompleted = hasSurvey || localCompleted;
  const needsSurvey =
    enforced &&
    !isCompleted &&
    (totalGenerations ?? 0) >= 1;

  return {
    loading: flagLoading || loading,
    needsSurvey,
    markCompleted,
  };
};
